import React from 'react';
import { labelFontSemiBold, vars } from '../../../../Theme';
import { Box, Checkbox, Chip, FormControlLabel, SxProps, Typography } from '@mui/material';
import { IIconProps } from '../../../../types';
import Icon from '@mdi/react';
import { mdiCheckboxOutline } from '@mdi/js';
import { TreeItem } from '@mui/x-tree-view';
import AppTooltip from '../../AppTooltip/AppTooltip';

const treeItemLabelSx: SxProps = {
    ...labelFontSemiBold,
    color: vars.font,
    fontWeight: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    height: '40px',
    alignContent: 'center',
};

type DualCheckState = boolean | 'halfChecked';

interface ICheckboxTreeItemProps {
    id: string;
    label: string;
    isChecked?: DualCheckState | ((id: string) => DualCheckState);
    onCheck?: (id: string, newValue: boolean) => void;
    children?: ICheckboxTreeItemProps[];
    iconConfig?: {
        parentIcon: IIconProps;
        childIcon: IIconProps;
        skeletonIcon: IIconProps;
    };
    isSkeleton?: boolean;
    notFound?: boolean;
    hasCheckbox?: boolean;
    chipLabel?: string;
}

export const CheckboxTreeItem = (props: ICheckboxTreeItemProps) => {
    const {
        id,
        label,
        iconConfig,
        isChecked,
        children,
        onCheck,
        isSkeleton,
        notFound,
        hasCheckbox = true,
        chipLabel,
    } = props;
    const checked = typeof isChecked === 'function' ? isChecked(id) : isChecked;

    const childrenDependentIcon =
        children?.length > 0 ? iconConfig?.parentIcon : iconConfig?.childIcon;
    const icon = isSkeleton ? iconConfig?.skeletonIcon : childrenDependentIcon;

    return (
        <TreeItem
            key={id}
            itemId={id}
            sx={
                children?.length > 0
                    ? {
                          '& .MuiTreeItem-content': {
                              p: '0px 12px',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                          },
                      }
                    : {
                          '& .MuiTreeItem-iconContainer': {
                              display: 'none', // Hide the icon container if no children
                          },

                          '& .MuiTreeItem-content': {
                              p: '0px 36px',
                          },
                      }
            }
            label={
                <Box
                    component='label'
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    {!notFound && hasCheckbox && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    key={id}
                                    disabled={isSkeleton}
                                    checked={Boolean(checked)}
                                    onChange={ev => onCheck(id, ev.target.checked)}
                                    onClick={ev => ev.stopPropagation()}
                                    sx={{ p: '8px' }}
                                    checkedIcon={
                                        checked === 'halfChecked' ? (
                                            <Icon
                                                path={mdiCheckboxOutline}
                                                color={vars.brandGreen}
                                                size={1}
                                            />
                                        ) : undefined
                                    }
                                />
                            }
                            label={<></>}
                            sx={{ marginRight: 0 }}
                        />
                    )}
                    {icon && (
                        <Icon
                            path={icon.path}
                            color={icon.color}
                            size={1}
                            style={{ marginRight: '8px', minHeight: '24px', minWidth: '24px' }}
                        />
                    )}
                    <AppTooltip title={label}>
                        <Typography sx={treeItemLabelSx}>{label}</Typography>
                    </AppTooltip>
                    {chipLabel && (
                        <Chip
                            sx={{
                                ...labelFontSemiBold,
                                color: vars.white,
                                backgroundColor: vars.brandDarkBlueSecondary,
                                marginLeft: 'auto',
                                height: '26px',
                            }}
                            label={chipLabel}
                        />
                    )}
                </Box>
            }
        >
            {(children ?? []).map(childProps => (
                <CheckboxTreeItem
                    {...props}
                    {...childProps}
                    key={childProps.id}
                    isSkeleton={!childProps.id}
                />
            ))}
        </TreeItem>
    );
};
